import { readLocalStorageValue } from '@mantine/hooks';
import { OpenEnrollmentPeriodDto } from '@zorro/clients';
import { getNow, parseDateISO } from '@zorro/shared/formatters';
import maxBy from 'lodash/maxBy';

export const getSelectedOpenEnrollmentPeriodStorageKey = (employerId: string) =>
  `selectedOpenEnrollmentPeriodId_${employerId}`;

type OpenEnrollmentPeriod = {
  id: string;
  effectiveFrom: string;
  effectiveUntil: string;
};

/**
 * Finds the selected open enrollment period based on localStorage preference
 * or falls back to current/most recent period
 */
export function getSelectedOpenEnrollmentPeriod(
  openEnrollmentPeriods: OpenEnrollmentPeriod[],
  employerId?: string
): OpenEnrollmentPeriod | null {
  if (!employerId || !openEnrollmentPeriods.length) {
    return null;
  }

  const storedOpenEnrollmentPeriodId = readLocalStorageValue<string>({
    key: getSelectedOpenEnrollmentPeriodStorageKey(employerId),
    defaultValue: '',
  });

  let selectedOpenEnrollmentPeriod = null;

  if (storedOpenEnrollmentPeriodId) {
    // First priority: use the stored/selected OEP from localStorage
    selectedOpenEnrollmentPeriod = openEnrollmentPeriods.find(
      (oep) => oep.id === storedOpenEnrollmentPeriodId
    );
  }

  if (!selectedOpenEnrollmentPeriod) {
    // Fallback: find current or most recent OEP
    selectedOpenEnrollmentPeriod =
      openEnrollmentPeriods.find((oep) =>
        getNow().isBetween(
          parseDateISO(oep.effectiveFrom),
          parseDateISO(oep.effectiveUntil)
        )
      ) ||
      maxBy(openEnrollmentPeriods, (oep) =>
        parseDateISO(oep.effectiveFrom).year()
      );
  }

  return selectedOpenEnrollmentPeriod || null;
}

/**
 * Gets the class definition year from the selected open enrollment period
 */
export function getClassDefinitionYear(
  openEnrollmentPeriods: OpenEnrollmentPeriod[],
  employerId?: string
): number {
  const selectedOpenEnrollmentPeriod = getSelectedOpenEnrollmentPeriod(
    openEnrollmentPeriods,
    employerId
  );

  return selectedOpenEnrollmentPeriod?.effectiveFrom
    ? parseDateISO(selectedOpenEnrollmentPeriod.effectiveFrom).year()
    : getNow().year();
}

/**
 * Finds the open enrollment period with the latest effectiveFrom date
 */
export function getLatestOpenEnrollmentPeriod(
  openEnrollmentPeriods: OpenEnrollmentPeriodDto[]
): OpenEnrollmentPeriodDto | undefined {
  if (openEnrollmentPeriods.length === 0) {
    return undefined;
  }

  return openEnrollmentPeriods.reduce((latest, current) => {
    return parseDateISO(current.effectiveFrom).isAfter(
      parseDateISO(latest.effectiveFrom)
    )
      ? current
      : latest;
  });
}

/**
 * Finds the open enrollment period with the earliest effectiveFrom date
 */
export function getEarliestOpenEnrollmentPeriod(
  openEnrollmentPeriods: OpenEnrollmentPeriodDto[]
): OpenEnrollmentPeriodDto | undefined {
  if (openEnrollmentPeriods.length === 0) {
    return undefined;
  }

  return openEnrollmentPeriods.reduce((earliest, current) => {
    return parseDateISO(current.effectiveFrom).isBefore(
      parseDateISO(earliest.effectiveFrom)
    )
      ? current
      : earliest;
  });
}
